import { AfterViewInit, Component, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { ColumnApi, GridApi, GridOptions, GridReadyEvent } from "ag-grid-community";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { EditDeleteActionsRenderer } from "src/app/components/shared/ag-grid/cell-renderers/edit-delete.component";
import { Utilities } from "src/app/models/utilities";
import { AppCoreService } from "src/app/services/app-core.service";
import { GeoService } from "src/app/services/geo.service";
import { ProductService } from "src/app/services/products.service";
import { VendorService } from "src/app/services/vendor.service";
import Swal from "sweetalert2";


@Component({
  selector: 'api-config',
  templateUrl: './api-config.component.html'
})
export class APIConfigComponent implements AfterViewInit {
  apiConfigForm: FormGroup;
  vendorId: number;
  id: number;
  permConfig = {
    isViewAllowed: false,
    isAddAllowed: false,
    isEditAllowed: false,
    isDeleteAllowed: false
  };
  isFormSubmitted = false;
  accCodeList = [];
  countryCode: any;
  serviceList: any[] = [];
  productList: any[] = [];
  countryList: any[] = [];
  originStateList: any[] = [];
  originCityList: any[] = [];
  originPincodeList: any[] = [];
  destinationStateList: any[] = [];
  destinationCityList: any[] = [];
  editMode = false;
  vendorDetails: any;
  apiConfigId: number;
  private gridApi!: GridApi;
  private gridColumnApi!: ColumnApi;
  public context: any;
  defaultColDef = {
    sortable: true,
    resizable: false,
  };
  gridOptions: GridOptions = {
    pagination: true,
    paginationPageSize: 10,
    domLayout: 'autoHeight',
  }
  columnDefs = [
    { headerName: 'Account Code', field: 'accountcode', filter: true, minWidth: 150 },
    {
      headerName: 'Origin',
      children: [
        { headerName: 'Country', field: 'origincountry', filter: true },
        { headerName: 'State', field: 'originstate', filter: true },
        { headerName: 'City', field: 'origincity', filter: true },
      ],
    },
    {
      headerName: 'Destination',
      children: [
        { headerName: 'Country', field: 'destinationcountry', filter: true },
        { headerName: 'State', field: 'destinationstate', filter: true },
        { headerName: 'City', field: 'destinationcity', filter: true },
      ],
    },
    { headerName: 'Service', field: 'servicename', filter: true },
    { headerName: 'Product', field: 'product', filter: true },
    { headerName: 'Min Weight', field: 'minweight', filter: true },
    { headerName: 'Max Weight', field: 'maxweight', filter: true },
    { headerName: 'Api Service', field: 'apiservice', filter: true },
    { headerName: 'Api Packaging', field: 'apipackaging', filter: true },
    {
      headerName: 'Actions',
      field: 'id',
      cellRenderer: EditDeleteActionsRenderer,
      cellClass: 'actions-button-cell',
      cellStyle: { overflow: 'visible' },
      colId: 'params',
      editable: false,
      sortable: false,
    },
  ];


  rowData = [];
  useEnterKey: any;
  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private appCoreService: AppCoreService,
    private geoService: GeoService,
    private vendorService: VendorService,
    private ngxLoaderService: NgxUiLoaderService,
    private productService: ProductService,
    private titleService: Title,
    private utilities: Utilities,
  ) {
    titleService.setTitle('Vendors');
    this.context = { componentParent: this };
  }

  ngOnInit(): void {
    this.apiConfigForm = this.formBuilder.group({
      txtVendor: new FormControl({ value: null, disabled: true }, Validators.compose([Validators.required])),
      ddlAccCode: [null, Validators.required],
      ddlOriginCountry: [null],
      ddlOriginState: [null],
      ddlOriginCity: [null],
      ddlDestinationCountry: [null],
      ddlDestinationState: [null],
      ddlDestinationCity: [null],
      txtMinWeight: [null],
      txtMaxWeight: [null],
      ddlService: [null, Validators.required],
      ddlProduct: [null],
      txtApiService: [null],
      txtApiPackaging: [null],
    });
    this.vendorId = this.route.snapshot.params['id'];
    this.checkPermission();
    if (this.permConfig.isEditAllowed) {
      this.bindVendorDetailsById();
      this.bindCountries();
      this.getAllVendorServices(this.vendorId);
      this.getAllProducts();
      this.getAllAccountsByVendorId(this.vendorId);
      this.getAllApiConfigsByVendorId(this.vendorId);
    }
  }

  ngAfterViewInit(): void {
    this.useEnterKey ? this.utilities.focusFirstElement(this.apiConfigForm) : null;
  }

  checkPermission(): any {
    const userData = this.appCoreService.GetUserData();
    this.useEnterKey = !userData.metaInfo.tabbasedtemplate;

    const permission = userData.config.filter(x => x.categoryname === 'Vendors');
    if (permission.length) {
      this.permConfig.isViewAllowed = permission[0].view;
      this.permConfig.isAddAllowed = permission[0].add;
      this.permConfig.isEditAllowed = permission[0].edit;
      this.permConfig.isDeleteAllowed = permission[0].delete;
    }
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    params.api.sizeColumnsToFit();
  }

  getAllAccountsByVendorId(id: any) {
    try {
      this.vendorService.GetAllAccountsByVendorId(id).subscribe((res) => {
        if (res && res.success) {
          this.accCodeList = res.data;
        }
      })
    } catch (error) {

    }
  }

  async openConfirmationDialog(msg: any, id?: number) {
    const result = await Swal.fire({
      titleText: 'Confirmation',
      text: msg,
      icon: 'warning',
      confirmButtonText: 'Yes',
      showDenyButton: true,
      denyButtonText: 'No',
      allowEscapeKey: false,
      allowOutsideClick: false
    });
    if (result.isConfirmed) {
      this.deleteRecord(id);
      this.getAllApiConfigsByVendorId(this.vendorId);
    }
  }

  getAllProducts() {
    try {
      this.productService.GetAll({ isactive: this.editMode ? false : true }).subscribe((res) => {
        if (res && res.success) {
          this.productList = res.data;
        }
      })
    } catch (error) {

    }
  }

  bindVendorDetailsById() {
    this.vendorService.GetById(this.vendorId).subscribe((res) => {
      this.ngxLoaderService.start();
      try {
        if (res && res.success) {
          this.ngxLoaderService.stop();
          // console.log(res);
          this.vendorDetails = res.data[0];
          this.apiConfigForm.controls.txtVendor.setValue(res.data[0]?.name);
          this.ngxLoaderService.stop();
        }
      } catch (error) {
        this.ngxLoaderService.stop();
      }

    })
  }

  upsert() {
    if (!this.permConfig.isAddAllowed && !this.editMode) {
      this.warningDialog('No access to Add.');
      return;
    }
    else if (!this.permConfig.isEditAllowed && this.editMode) {
      this.warningDialog('No access to Edit.');
      return;
    }
    this.isFormSubmitted = true;
    if (this.apiConfigForm.invalid) {
      // console.log(this.apiConfigForm);
    }
    if (this.permConfig.isAddAllowed && this.apiConfigForm.valid || this.permConfig.isEditAllowed && this.apiConfigForm.valid) {
      const forwardingdetails = {
        apiservice: this.apiConfigForm.controls.txtApiService.value,
        apipackaging: this.apiConfigForm.controls.txtApiPackaging.value
      }
      const reqObj = {
        id: this.apiConfigId || null,
        vendorCode: this.vendorDetails.code,
        accountCode: this.apiConfigForm.controls.ddlAccCode.value,
        originCountryCode: this.apiConfigForm.controls.ddlOriginCountry.value,
        originStateCode: this.apiConfigForm.controls.ddlOriginState.value,
        originCityCode: this.apiConfigForm.controls.ddlOriginCity.value,
        destinationCountryCode: this.apiConfigForm.controls.ddlDestinationCountry.value,
        destinationStateCode: this.apiConfigForm.controls.ddlDestinationState.value,
        destinationCityCode: this.apiConfigForm.controls.ddlDestinationCity.value,
        minWeight: this.apiConfigForm.controls.txtMinWeight.value,
        maxWeight: this.apiConfigForm.controls.txtMaxWeight.value,
        serviceName: this.apiConfigForm.controls.ddlService.value,
        productCode: this.apiConfigForm.controls.ddlProduct.value,
        forwardingdetails: forwardingdetails
      }
      // console.log('reqObj', reqObj);
      if (parseFloat(reqObj.minWeight) > parseFloat(reqObj.maxWeight)) {
        this.errorDialog('Min Weight cannot be Greater than Max Weight', 'info');
        return;
      }
      if (this.editMode) {
        this.update(reqObj);
      } else
        this.add(reqObj)
    }
  }

  add(reqObj: any) {
    this.ngxLoaderService.start();
    this.vendorService.addApiConfig(reqObj).subscribe((res) => {
      try {
        if (res && !res.success) {
          this.ngxLoaderService.stop();
          this.errorDialog(res.message, 'error');
        }
        if (res && res.success) {
          this.ngxLoaderService.stop();
          this.openSuccessDialog("Record added successfully");
        }
      }
      catch (err) {
        console.error(err);
      }
    });
  }

  update(reqObj: any) {
    this.ngxLoaderService.start();
    this.vendorService.updateApiConfig(reqObj).subscribe((res) => {
      try {
        if (res && !res.success) {
          this.ngxLoaderService.stop();
          this.errorDialog(res.message, 'error');
        }
        if (res && res.success) {
          this.ngxLoaderService.stop();
          this.openSuccessDialog("Record updated successfully");
        }
      }
      catch (err) {
        console.error(err);
      }
    });
  }

  getAllVendorServices(vendorid?: any, vendorChanged?: any) {
    if (vendorid) {
      if (vendorChanged)
        this.apiConfigForm.controls.ddlService.reset();
      try {
        this.vendorService.GetAllListByVendor({ id: vendorid }).subscribe((res) => {
          if (res && res.success) {
            this.serviceList = res.data;
          }
        })
      } catch (e) {
        console.error(e);
      }
    }
  }

  async editAction(cell: any) {
    // console.log(cell);
    try {
      if (!this.permConfig.isEditAllowed) {
        this.warningDialog('No access to Edit.');
      }
      else if (this.permConfig.isEditAllowed && cell) {
        this.apiConfigId = cell.id;
        this.apiConfigForm.controls.ddlOriginCountry.setValue(cell.origincountrycode);
        this.apiConfigForm.controls.ddlOriginState.setValue(cell.originstatecode);
        this.apiConfigForm.controls.ddlOriginCity.setValue(cell.origincitycode);
        this.apiConfigForm.controls.ddlDestinationCountry.setValue(cell.destinationcountrycode);
        this.apiConfigForm.controls.ddlDestinationState.setValue(cell.destinationstatecode);
        this.apiConfigForm.controls.ddlDestinationCity.setValue(cell.destinationcitycode);
        this.bindStatesByCountry(cell?.origincountrycode, 'origin');
        this.bindCitiesByState(cell?.originstatecode, 'origin');
        this.bindStatesByCountry(cell?.destinationcountrycode, 'destination');
        this.bindCitiesByState(cell?.destinationstatecode, 'destination');
        this.apiConfigForm.patchValue({
          ddlAccCode: cell.accountcode,
          ddlService: cell.servicename,
          ddlProduct: cell.productcode,
          txtMinWeight: cell.minweight,
          txtMaxWeight: cell.maxweight,
          txtApiService: cell.apiservice,
          txtApiPackaging: cell.apipackaging,
        });
        this.editMode = true;
        this.apiConfigForm.controls.ddlAccCode.disable();
        this.getAllProducts();
      }
    } catch (e) {
      console.error(e);
    }
  }

  deleteAction(cell: any) {
    // console.log(cell);
    if (!this.permConfig.isDeleteAllowed) {
      this.warningDialog('No access to Delete.');
    }
    else if (this.permConfig.isDeleteAllowed && cell && cell.id) {
      this.openConfirmationDialog('Are you sure you want to delete ?', cell.id);
    }
  }

  deleteRecord(id) {
    try {
      if (id) {
        this.vendorService.DeleteApiConfigById(id).subscribe((res) => {
          if (res && res.success) {
            this.getAllApiConfigsByVendorId(this.vendorId);
          }
        });
      }
    } catch (e) {
      console.error(e);
    }
  }

  onOriginCountryChange() {
    const countryCode = this.apiConfigForm.get('ddlOriginCountry').value;
    this.apiConfigForm.controls.ddlOriginState.reset();
    this.apiConfigForm.controls.ddlOriginCity.reset();
    this.originStateList = [];
    this.originCityList = [];
    if (countryCode) {
      this.bindStatesByCountry(countryCode, 'origin');
    }
  }

  onDestinationCountryChange() {
    const countryCode = this.apiConfigForm.get('ddlDestinationCountry').value;
    this.apiConfigForm.controls.ddlDestinationState.reset();
    this.apiConfigForm.controls.ddlDestinationCity.reset();
    this.destinationStateList = [];
    this.destinationCityList = [];
    if (countryCode) {
      this.bindStatesByCountry(countryCode, 'destination');
    }
  }

  bindStatesByCountry(countryCode?: string, type?: any) {
    try {
      if (countryCode) {
        this.geoService.GetAllStates({ countrycode: countryCode }).subscribe((res) => {
          if (res && res.success) {
            this.countryCode = countryCode;
            if (type === 'origin') {
              this.originStateList = res.data;
            } else {
              this.destinationStateList = res.data;
            }
          }
        });
      }

    } catch (e) {
      console.error(e);
    }
  }

  onOriginStateChange() {
    const countryCode = this.apiConfigForm.get('ddlOriginState').value;
    this.apiConfigForm.controls.ddlOriginCity.reset();
    this.originCityList = [];
    if (countryCode) {
      this.bindCitiesByState(countryCode, 'origin');
    }
  }

  onDestinationStateChange() {
    const countryCode = this.apiConfigForm.get('ddlDestinationState').value;
    this.apiConfigForm.controls.ddlDestinationCity.reset();
    this.destinationCityList = [];
    if (countryCode) {
      this.bindCitiesByState(countryCode, 'destination');
    }
  }

  bindCountries() {
    try {
      this.geoService.GetAllCountries().subscribe((res) => {
        if (res && res.success) {
          this.countryList = res.data;
        }
      });
    } catch (e) {
      console.error(e);
    }
  }

  bindCitiesByState(stateCode?: string, type?: any) {
    try {
      if (stateCode) {
        this.geoService.GetAllCities({ statecode: stateCode }).subscribe((res) => {
          if (res && res.success) {
            this[type === 'origin' ? 'originCityList' : 'destinationCityList'] = res.data;
          }
        });
      }
    } catch (e) {
      console.error(e);
    }
  }

  async openSuccessDialog(msg: any) {
    const result = await Swal.fire({
      title: 'Success!',
      text: msg,
      icon: 'success',
      confirmButtonText: 'OK',
      allowEscapeKey: false,
      allowOutsideClick: false
    })
    if (result.isConfirmed) {
      this.editMode = false;
      this.getAllApiConfigsByVendorId(this.vendorId);
      this.resetForm();
    }
  }

  getAllApiConfigsByVendorId(vendorId: number) {
    this.ngxLoaderService.start();
    this.vendorService.GetAllApiConfigsByVendorId(vendorId).subscribe((res) => {
      try {
        if (res && res.success) {
          this.ngxLoaderService.stop();
          this.rowData = res.data;
        }
      } catch (error) {
        this.ngxLoaderService.stop();
        console.error(error)
      }
    });
  }

  errorDialog(msg: any, icon: any) {
    Swal.fire({
      title: 'Opps!',
      text: msg,
      icon: icon,
      allowEscapeKey: false,
      allowOutsideClick: false
    })
  }

  warningDialog(msg: any) {
    Swal.fire({
      title: 'Warning!',
      text: msg,
      icon: 'warning',
      confirmButtonText: 'OK',
      allowEscapeKey: false,
      allowOutsideClick: false
    });
  }

  resetForm() {
    this.editMode = false;
    this.apiConfigForm.controls.ddlAccCode.enable();
    this.apiConfigForm.controls.ddlAccCode.setValidators([Validators.required]);
    this.apiConfigForm.controls.ddlAccCode.updateValueAndValidity();
    const vendor = this.apiConfigForm.controls.txtVendor.value;
    this.apiConfigForm.reset();
    this.apiConfigForm.controls.txtVendor.setValue(vendor);
    this.isFormSubmitted = false;
  }

}
